import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Chip, CircularProgress, Alert, IconButton, Tooltip,
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import { OrgChartCanvas } from '../components/company/OrgChart';

const API_URL = process.env.REACT_APP_API_URL || '';

// Pamje publike (pa login) e organigramit — për shfaqje në monitor/recepsion.
export default function PublicOrgChart() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetch(`${API_URL}/api/public/org-chart/${token}`)
      .then(r => r.json())
      .then(res => {
        if (!alive) return;
        if (res?.success) setData(res.data);
        else setError(res?.error || 'Organigrami nuk është publik ose linku është i pavlefshëm.');
      })
      .catch(() => alive && setError('Gabim gjatë ngarkimit.'))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [token]);

  const companyName = data?.companyName || 'Organigrami';
  const workers = data?.workers || [];
  const regularDepts = (data?.departments || []).filter(d => d.code !== 'EXEC');

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', p: { xs: 1.5, sm: 3 }, bgcolor: 'background.default' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2, flexShrink: 0 }}>
        <AccountTreeIcon color="primary" sx={{ fontSize: 24 }} />
        <Typography variant="h6" fontWeight={800} sx={{ fontSize: { xs: '1.05rem', sm: '1.3rem' } }}>
          {companyName}
        </Typography>
        {!loading && !error && (
          <Chip
            label={`${workers.length} punonjës · ${regularDepts.length} dep.`}
            size="small" variant="outlined" color="primary"
            sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}
          />
        )}
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="Zoom in">
            <IconButton size="small" onClick={() => setZoom(z => Math.min(z + 0.15, 2))}><ZoomInIcon /></IconButton>
          </Tooltip>
          <Tooltip title="Zoom out">
            <IconButton size="small" onClick={() => setZoom(z => Math.max(z - 0.15, 0.3))}><ZoomOutIcon /></IconButton>
          </Tooltip>
          <Tooltip title="Rivendos">
            <IconButton size="small" onClick={() => setZoom(1)}><FitScreenIcon /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Chart */}
      {error ? (
        <Alert severity="error">{error}</Alert>
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : (
        <OrgChartCanvas
          data={data}
          companyName={companyName}
          currentWorkerId={null}
          searchQuery=""
          zoom={zoom}
          loading={false}
          error=""
          showCounts={data?.options?.showCounts !== false}
        />
      )}
    </Box>
  );
}
